import React, { useState, useRef } from 'react';
import {
    Upload,
    FileText,
    Trash2,
    Loader2,
    X
} from 'lucide-react';

import { useToast } from './toastContext';
import { formatFileSize } from './houseHelpers';

const UploadActModal = ({ actType, onClose, onUpload }) => {
    const toast = useToast();

    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const fileInputRef = useRef(null);

    const actTypeNames = {
        commissioning: 'Акт ввода в эксплуатацию',
        decommissioning: 'Акт вывода из эксплуатации',
        other: 'Документ'
    };

    const validateFile = (file) => {
        if (file.size > 10 * 1024 * 1024) {
            toast.warning('Размер файла не должен превышать 10 MB');
            return false;
        }

        return true;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file && validateFile(file)) {
            setSelectedFile(file);
        }
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isDragging) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;

        if (files && files.length > 0) {
            const file = files[0];

            if (validateFile(file)) {
                setSelectedFile(file);
            }
        }
    };

    const handleZoneClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = (e) => {
        e.stopPropagation();

        setSelectedFile(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            toast.warning('Выберите файл');
            return;
        }

        setIsUploading(true);

        try {
            await onUpload(selectedFile);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <Upload className="w-5 h-5 text-orange-400" />
                        Загрузка: {actTypeNames[actType]}
                    </h3>

                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div>
                        <label className="block text-slate-400 text-xs mb-2">
                            Выберите файл (макс. 10 MB)
                        </label>

                        <div
                            onClick={handleZoneClick}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`
                                border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                                ${
                                    isDragging
                                        ? 'border-orange-500 bg-orange-500/10 scale-[1.02]'
                                        : selectedFile
                                            ? 'border-green-500/50 bg-green-500/5 hover:border-green-500'
                                            : 'border-slate-600 hover:border-orange-500'
                                }
                            `}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.rar"
                                onChange={handleFileChange}
                                className="hidden"
                            />

                            {selectedFile ? (
                                <div className="space-y-2">
                                    <FileText className="w-12 h-12 text-green-400 mx-auto" />

                                    <div>
                                        <p className="text-white font-medium text-sm truncate">
                                            {selectedFile.name}
                                        </p>

                                        <p className="text-slate-400 text-xs mt-1">
                                            {formatFileSize(selectedFile.size)}
                                        </p>
                                    </div>

                                    <button
                                        onClick={handleRemoveFile}
                                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-300 rounded text-xs transition"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Удалить
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Upload
                                        className={`w-12 h-12 mx-auto transition-colors ${
                                            isDragging ? 'text-orange-400' : 'text-slate-500'
                                        }`}
                                    />

                                    <p className="text-slate-400 text-sm">
                                        {isDragging
                                            ? 'Отпустите файл для загрузки'
                                            : 'Перетащите файл сюда или нажмите для выбора'}
                                    </p>

                                    <p className="text-slate-500 text-xs">
                                        PDF, Word, Excel, изображения, архивы
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-slate-700 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                    >
                        Отмена
                    </button>

                    <button
                        onClick={handleUpload}
                        disabled={isUploading || !selectedFile}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Загрузка...
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                Загрузить
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadActModal;